import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { redeemMaintain, type MaintainResult } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { useDelayedFlag } from "@/lib/useDelayedFlag";

// Landing after a "Claim & maintain" verification. The GitHub callback verified the creator, applied
// the badge, and set an httpOnly cookie referencing a one-time grant. Logged in → redeem it (transfer
// ownership). Not logged in → go to login (its Sign-up link carries the return path); after auth the
// user comes back here and the grant is redeemed automatically.
export default function ClaimMaintain() {
  useDocumentTitle("Maintain · SkillPatch");
  const { accessToken, loading } = useAuth();

  const [result, setResult] = useState<MaintainResult | null>(null);
  const [failed, setFailed] = useState(false);
  const redeemed = useRef(false);

  useEffect(() => {
    if (loading || !accessToken || redeemed.current) return;
    redeemed.current = true;
    redeemMaintain(accessToken)
      .then(setResult)
      .catch(() => setFailed(true));
  }, [accessToken, loading]);

  // Defer the loading placeholder so a fast session restore goes straight through without a flash.
  const showLoading = useDelayedFlag(loading);

  if (loading) return showLoading ? <Centered>Loading…</Centered> : null;
  // Not signed in → straight to login (no grant in the URL — it rides the cookie).
  if (!accessToken) {
    return <Navigate to={`/login?next=${encodeURIComponent("/claim/maintain")}`} replace />;
  }
  if (failed) {
    return (
      <Shell tone="warning" title="Couldn’t complete the transfer">
        <p className="text-text/60">Something went wrong. Please try again from the skill page.</p>
        <Actions><Link to="/home" className={primaryBtn}>Go to home</Link></Actions>
      </Shell>
    );
  }
  if (!result) return <Centered>Transferring ownership…</Centered>;

  const { owned, already_owned, skipped, failed_count } = result;
  const failNote =
    failed_count > 0 ? (
      <p className="mt-3 text-sm text-amber-300/80">
        {failed_count} repositor{failed_count === 1 ? "y" : "ies"} couldn’t be verified and {failed_count === 1 ? "was" : "were"} skipped.
      </p>
    ) : null;

  // Freshly transferred (may be a mix with already-owned).
  const total = owned.length + already_owned.length;
  if (total > 0) {
    const only = total === 1 ? owned[0] ?? already_owned[0] : null;
    const verb = owned.length > 0 ? "now maintain" : "already maintain";
    return (
      <Shell tone="success" title={total === 1 ? `You ${verb} this skill` : `You ${verb} ${total} skills`}>
        <p className="text-text/60">
          It’s a normal skill you own now — edit it, publish new versions, manage suggestions, and link
          it to GitHub for sync whenever you like.
        </p>
        {failNote}
        <Actions>
          {only ? (
            <Link to={`/skill/${only}`} className={primaryBtn}>Open your skill</Link>
          ) : (
            <Link to="/home" className={primaryBtn}>Go to home</Link>
          )}
        </Actions>
      </Shell>
    );
  }

  // Nothing transferred: either the grant is gone/expired, or the skills were claimed by someone else.
  return (
    <Shell tone="warning" title="Nothing to claim here">
      <p className="text-text/60">
        {skipped.length > 0
          ? "These skills are no longer available to claim."
          : "This claim link has expired or was already used."}
      </p>
      <Actions><Link to="/skills" className={primaryBtn}>Browse skills</Link></Actions>
    </Shell>
  );
}

const primaryBtn =
  "rounded-[12px] bg-primary px-6 py-2.5 font-display font-semibold text-white shadow-btn transition-all duration-200 hover:bg-primary-hover hover:scale-[1.04] hover:shadow-[0_0_28px_rgba(225,100,46,0.5)] active:scale-[0.98]";

function Centered({ children }: { children: React.ReactNode }) {
  return <main className="mx-auto flex min-h-[60vh] max-w-xl items-center justify-center px-6 text-text/50">{children}</main>;
}

function Actions({ children }: { children: React.ReactNode }) {
  return <div className="mt-6 flex items-center justify-center gap-3">{children}</div>;
}

function Shell({ tone, title, children }: { tone: "success" | "warning"; title: string; children: React.ReactNode }) {
  const ring =
    tone === "success"
      ? "border-green-500/30 bg-green-500/10 text-green-400"
      : "border-amber-500/30 bg-amber-500/10 text-amber-400";
  return (
    <main className="mx-auto flex min-h-[70vh] max-w-xl flex-col items-center justify-center px-6 text-center">
      <div className={`flex h-20 w-20 items-center justify-center rounded-2xl border ${ring}`}>
        {tone === "success" ? (
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
        ) : (
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01" /><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg>
        )}
      </div>
      <h1 className="mt-6 font-display text-2xl font-bold tracking-tight">{title}</h1>
      <div className="mt-3 max-w-md text-[15px] leading-relaxed">{children}</div>
    </main>
  );
}
